import { useCallback, useMemo } from 'react';
import Popover from '@mui/material/Popover';
import { bindPopover } from 'material-ui-popup-state/hooks';

import { MembershipActivityFilter, MembershipActivityFragment } from '@occc/graphql';
import { useGetMembershipActivities } from '@occc-app/dataAccessLayer/membership/queries';
import { DateTimeFormat, endOfDateTime, formatDateTime, startOfDateTime } from '@occc-app/lib/date';

import { MembershipActivity } from './membership-activity.component';
import {
  MembershipActivityFilterForm,
  MembershipActivityFilterFormProps,
} from './membership-activity-filter-form.component';
import {
  useEndDateFilter,
  usePaginationParams,
  usePointChangeTypeFilter,
  useStartDateFilter,
  useTypeFilter,
} from './membership-activity.hooks';
import { getPointChangeSourceTypeFilter } from './membership-activity.utils';
import { MembershipActivityFilterFormValues } from './membership-activity.types';

export type MembershipActivityContainerProps = {
  memberId: string;
  onMembershipActivityClick(item: MembershipActivityFragment): void;
  searchText?: string;
  popoverProps: ReturnType<typeof bindPopover>;
};

export const MembershipActivityContainer = ({
  memberId,
  onMembershipActivityClick,
  popoverProps,
  searchText,
}: MembershipActivityContainerProps) => {
  const [startDate, setStartDate] = useStartDateFilter();
  const [endDate, setEndDate] = useEndDateFilter();
  const [type, setType] = useTypeFilter();
  const [pointChangeType, setPointChangeType] = usePointChangeTypeFilter();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const handleReset = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setType('any');
    setPointChangeType('any');
    popoverProps.onClose();
  }, [popoverProps, setEndDate, setStartDate, setType, setPointChangeType]);

  const handleSubmit = useCallback<MembershipActivityFilterFormProps['onSubmit']>(
    ({ startDate, endDate, type, pointChangeType }) => {
      setStartDate(startDate);
      setEndDate(endDate);
      setType(type);
      setPointChangeType(pointChangeType);
      popoverProps.onClose();
    },
    [popoverProps, setEndDate, setStartDate, setType, setPointChangeType],
  );

  const filter: MembershipActivityFilter = useMemo(
    () => ({
      transactionCreatedAtMin: startDate
        ? formatDateTime(startOfDateTime(startDate, 'day'), DateTimeFormat.DateTimeISOUtc)
        : undefined,
      transactionCreatedAtMax: endDate
        ? formatDateTime(endOfDateTime(endDate, 'day'), DateTimeFormat.DateTimeISOUtc)
        : undefined,
      membershipActivityType: type === 'any' ? undefined : type,
      search: searchText,
      ...getPointChangeSourceTypeFilter(type, pointChangeType),
    }),
    [startDate, endDate, searchText, type, pointChangeType],
  );

  const { loading, error, data, paginationProps } = useGetMembershipActivities({
    membershipId: memberId,
    filter,
    paginationInfo,
    onPageChange,
  });

  const initialValues: MembershipActivityFilterFormValues = useMemo(() => {
    return {
      startDate,
      endDate,
      type,
      pointChangeType,
    };
  }, [startDate, endDate, type, pointChangeType]);

  return (
    <>
      <MembershipActivity
        activities={data}
        error={error}
        loading={loading}
        paginationProps={paginationProps}
        onRowItemClick={onMembershipActivityClick}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        PaperProps={{
          sx: { maxWidth: 460 },
        }}
      >
        <MembershipActivityFilterForm
          onSubmit={handleSubmit}
          onReset={handleReset}
          initialValues={initialValues}
        />
      </Popover>
    </>
  );
};
